import {FilterFactory, InFilter, TermFilter} from '@dofleini/query-builder';
import {CubaCIRuler, LatterAndNumberRuler} from '@dofleini/validator';
import {activityPlaces} from '@mep/pae-commons';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useQueries} from 'react-query';

import {DynamicFieldSelect} from '@/components/DynamicFormCreate/DynamicFormFields';
import {renderMunicipalitySelect} from '@/components/MunicipalitySelect';
import ProvinceSelect from '@/components/ProvinceSelect';
import {renderUploadFileDragged} from '@/components/UploadFileDragger';
import {CACHE_KEY_ACTIVITY_PLACE, CACHE_KEY_LIST_ACTIVITY_PLACE} from '@/modules/activityPlace/contants/activityPlace';
import {fields} from '@/modules/activityPlace/contants/activityPlace.form';
import ActivityPlaceApiService from '@/modules/activityPlace/services/ActivityPlaceApiService';
import ActivityPlaceTenantOriginSelect from '@/modules/proposalRequest/components/ActivityPlaceTenantOriginSelect';
import ProposalActivitiesSelect from '@/modules/proposalRequest/components/ProposalActivitiesSelect';
import {createFieldsHook} from '@/utils/createFieldsHook';
import {createGetOneHook} from '@/utils/createGetOneHook';
import createSelectOptionFromObject from '@/utils/createSelectOptionFromObject';
import {validations} from '@/utils/fields';
import {renderProvince} from '@/utils/location';

const {
  ACTIVITY_PLACE_LINK_TYPES_ENUM,
  ACTIVITY_PLACE_STATUS_ENUM,
  ACTIVITY_PLACE_TYPES,
  ACTIVITY_PLACE_TENANT_TYPES
} = activityPlaces;

export const useGetOneActivityPlace = createGetOneHook(ActivityPlaceApiService, 'id', CACHE_KEY_ACTIVITY_PLACE);
export const useFieldsBase = createFieldsHook('activityPlace', fields);

const proposalActivityPlaceFields = fields?.filter(e => e?.key !== 'proposal');
proposalActivityPlaceFields.push({
  key: 'proposal',
  noStyle: true,
  widgetProps: {
    hidden: true
  },
  required: true
});

export const useFieldsBaseForProposal = createFieldsHook('activityPlace', proposalActivityPlaceFields);

export const useFieldsBaseExt = (useFieldsHook = useFieldsBase, isViewMode, form, columns, isTCPProposal = false) => {
  const {t} = useTranslation('activityPlace');
  const fields = useFieldsHook(isViewMode, form, columns);
  
  const {proposal} = form?.getFieldsValue();
  
  return useMemo(() => {
    const fieldsMap = [...fields];
    
    const activities = proposal?.activities?.map(e => e?._id || e) || [];
    const mainActivity = proposal?.mainActivity?._id;
    
    if (!isViewMode && !isTCPProposal) {
      fieldsMap.splice(7, 0,
        {
          colSpan: 2,
          key: 'activities',
          label: t('activities'),
          placeholder: t('common:selectPlaceholder'),
          required: true,
          disabled: !proposal,
          widget: ProposalActivitiesSelect,
          widgetProps: {
            filters: new InFilter({field: '_id', value: activities?.concat([mainActivity])})
          },
        });
      return fieldsMap;
    }
    return fieldsMap;
  }, [fields, isViewMode, proposal, t, isTCPProposal]);
};

export const useFields = (isViewMode, form, columns) => {
  return useFieldsBaseExt(useFieldsBase, isViewMode, form, columns);
};

const translateOptions = (options, t) => options.map(opt => ({
  label: t(`types.${opt.label}`),
  value: opt.value,
}));

export const useFieldsForProposal = (isViewMode, form, columns, isTCPProposal, values) => {
  const {t} = useTranslation('activityPlace');
  const fields = useFieldsBaseExt(useFieldsBaseForProposal, isViewMode, form, columns, isTCPProposal);
  const {type, tenant, address} = form?.getFieldsValue();
  
  return useMemo(() => {
    let fieldsMap = [...fields];
    
    if (!isViewMode) {
      if (isTCPProposal) {
        fieldsMap.splice(0, 0, {
          label: t('form.type'),
          help: t('form.typeHelp'),
          colSpan: 3,
          key: 'type',
          required: true,
          widget: DynamicFieldSelect,
          widgetProps: {
            options: createSelectOptionFromObject(ACTIVITY_PLACE_TYPES)
              ?.filter(e => e?.value !== ACTIVITY_PLACE_TYPES.STANDARD),
            translate: 'activityPlace',
            mapOptions: translateOptions
          }
        });
        
        if ([
          ACTIVITY_PLACE_TYPES.AMBULATORY,
          ACTIVITY_PLACE_TYPES.LEASE_SPACE_AREA,
          ACTIVITY_PLACE_TYPES.PUBLIC_VIA,
        ].includes(type)) {
          fieldsMap = fieldsMap?.filter(e =>
            e?.key === 'type' ||
            e?.key === 'description' ||
            e?.key?.startsWith('address')
          ) || [];
          
          if (type === ACTIVITY_PLACE_TYPES.AMBULATORY)
            fieldsMap = fieldsMap?.filter(e => !e?.key?.startsWith('address')) || [];
          
          if (type !== ACTIVITY_PLACE_TYPES.PUBLIC_VIA)
            fieldsMap.splice(1, 0,
              {
                key: 'registryCertificate',
                label: t('agreementCAM'),
                required: true,
                colSpan: 4,
                render: renderUploadFileDragged,
                widgetProps: {
                  isImage: false,
                  listType: 'picture',
                  fileLabel: 'fileLabel',
                  maxCount: 1
                },
              });
        }
        if ([
          ACTIVITY_PLACE_TYPES.LEASE_ENABLED_AREA,
        ].includes(type)) {
          fieldsMap = fieldsMap?.filter(e =>
            e?.key === 'type' ||
            e?.key === 'registryCertificate' ||
            e?.key === 'description' ||
            e?.key?.startsWith('address')
          ) || [];
        }
        if ([
          ACTIVITY_PLACE_TYPES.SEA,
          ACTIVITY_PLACE_TYPES.TRANSPORT_PUBLIC_VIA,
          ACTIVITY_PLACE_TYPES.OWN_ADDRESS,
          ACTIVITY_PLACE_TYPES.CUSTOMER_ADDRESS,
        ].includes(type)) {
          fieldsMap = fieldsMap?.filter(e => e?.key === 'type' || e?.key === 'description') || [];
        }
        if ([
          ACTIVITY_PLACE_TYPES.OTHER,
        ].includes(type)) {
          fieldsMap = fieldsMap?.filter(e =>
            e?.key === 'type' ||
            e?.key === 'description' ||
            e?.key?.startsWith('address')
          ) || [];
          
          fieldsMap.splice(7, 0,
            {
              key: 'documents',
              label: t('common:documents'),
              required: false,
              colSpan: 4,
              render: renderUploadFileDragged,
              widgetProps: {
                isImage: false,
                multiple: true,
                listType: 'picture',
                fileLabel: 'fileLabel'
              },
            });
        }
        
        if ((address?.province || values?.partners?.[0]?.address?.province) === 'MTZ')
          fieldsMap.splice(fieldsMap?.length, 0, {
            colSpan: 3,
            key: 'isOnPeninsula9045',
            required: false,
            widget: 'radio-group',
            options: [[true, t('common:yes')], [false, t('common:no')]],
            label: t('form.isOnPeninsula9045'),
            widgetProps: {
              translate: 'activityPlace',
              mapOptions: translateOptions
            }
          });
        
        const positionField = fieldsMap?.length + 1;
        if ([
          ACTIVITY_PLACE_TYPES.LEASE_ENABLED_AREA,
          ACTIVITY_PLACE_TYPES.LEASE_SPACE_AREA,
        ].includes(type)) {
          fieldsMap.splice(6, 0,
            {
              key: 'tenant.origin',
              label: t('origins.label'),
              widget: ActivityPlaceTenantOriginSelect,
              required: true,
              colSpan: 3
            }
          );
          if (tenant?.origin)
            fieldsMap.splice(positionField + 1, 0,
              {
                key: 'tenant.fullName',
                colSpan: 3,
                label: t('origins.fullName'),
                placeholder: t('defaultPlaceholder'),
                required: true,
                widgetProps: {
                  size: 'large',
                  type: 'text',
                },
                rules: [LatterAndNumberRuler]
              },
              {
                key: 'tenant.address.province',
                required: true,
                colSpan: 1,
                label: t('province'),
                initialValue: address?.province,
                widget: ProvinceSelect,
                widgetProps: {
                  size: 'large',
                  onChange: () => form.setFieldsValue({bankType: '', bankOffice: ''})
                },
                viewWidget: ({value}) => renderProvince(value)
              },
              {
                key: 'tenant.address.municipality',
                label: t('municipality'),
                required: true,
                onlyEdit: true,
                related: 'tenant.address.province',
                initialValue: address?.municipality,
                colSpan: 1,
                widgetProps: {
                  size: 'large',
                },
                render: ({...rest}) => {
                  return renderMunicipalitySelect(rest, tenant?.address?.province);
                },
                rules: [validations.text()]
              },
              {
                colSpan: 2,
                key: 'tenant.address.abstract',
                label: t('address'),
                required: true,
                widget: 'textarea',
                widgetProps: {
                  size: 'large',
                },
              },
            );
          if (tenant?.origin === ACTIVITY_PLACE_TENANT_TYPES.NATURAL_PERSON)
            fieldsMap.splice(positionField + 5, 0,
              {
                key: 'tenant.idNumber',
                colSpan: 2,
                label: t('idNumber'),
                rules: [{required: true}, CubaCIRuler],
                widgetProps: {
                  size: 'large',
                },
              },
            );
          if (tenant?.origin === ACTIVITY_PLACE_TENANT_TYPES.LEGAL_ENTITY)
            fieldsMap.splice(positionField + 5, 0,
              {
                key: 'tenant.nit',
                colSpan: 2,
                label: t('nit'),
                rules: [{required: true}],
                widgetProps: {
                  size: 'large',
                },
              },
            );
        }
      }
    }
    return fieldsMap;
  }, [address?.municipality, address?.province, fields, form, isTCPProposal, isViewMode, t, tenant?.address?.province, tenant?.origin, type, values?.partners]);
};

export const useActivityPlacesByLinkTypes = (filters, search) => {
  const queries = ACTIVITY_PLACE_LINK_TYPES_ENUM?.map((type) => ({
    queryKey: [CACHE_KEY_LIST_ACTIVITY_PLACE, type, filters, search],
    queryFn: async () => {
      const {data} = await ActivityPlaceApiService.count({
        search,
        filters: FilterFactory.add(filters, new TermFilter({field: 'linkType', value: type})).toQuery(),
      }
      );
      return {[type]: data?.total};
    },
    refetchOnWindowFocus: false
  }));
  
  const responses = useQueries(queries);
  
  return {
    data: responses.map((r) => {
      return r?.data;
    })?.reduce((r, e) => {
      return ({...r, ...e});
    }, {}),
    isLoading: responses.some(e => e?.isLoading)
  };
};

export const useActivityPlacesByStatus = (filters, search) => {
  const queries = ACTIVITY_PLACE_STATUS_ENUM?.map((status) => ({
    queryKey: [CACHE_KEY_LIST_ACTIVITY_PLACE, status, filters, search],
    queryFn: async () => {
      const {data} = await ActivityPlaceApiService.count({
        search,
        filters: FilterFactory.add(filters, new TermFilter({field: 'status', value: status})).toQuery(),
      }
      );
      return {[status]: data?.total};
    },
    refetchOnWindowFocus: false
  }));
  
  const responses = useQueries(queries);
  
  return {
    data: responses.map((r) => {
      return r?.data;
    })?.reduce((r, e) => {
      return ({...r, ...e});
    }, {}),
    isLoading: responses.some(e => e?.isLoading)
  };
};
